import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../include/api';
import { ERROR, SUCCESS } from '../../include/constant';
import { preventClick } from '../../include/function';
import { useNotification } from '../../Toast/ToastProvider';
import { generateToken } from '../notifications/firebase';

const AdminLogin = ({ handleAdmin }) => {
    const toast = useNotification();

    // States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [otpInput, setOtpInput] = useState('');
    const [otpError, setOtpError] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [postLoading, setPostLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [currentView, setCurrentView] = useState('login');

    const handlePasswordChange = val => {
        setPasswordError('');
        setPassword(val);
    };

    const clearFields = () => {
        setPassword('');
        setPasswordError('');
    };

    const handleShowPassword = () => setShowPassword(!showPassword);

    const handleStoreCmToken = async cid => {
        const token = await generateToken();
        const data = { cid, token };
        api.post('/fcm/', data)
            .then(response => {
                if (response.status === 200) {
                    toast({ type: SUCCESS, message: response.data.message });
                }
            })
            .catch(error => {
                error.response
                    ? toast({ type: ERROR, message: error.response.data.message })
                    : toast({ type: ERROR, message: error.message });
            })
            .finally(() => {
                setPostLoading(false);
            });
    };

    const handleSignIn = () => {
        let error = false;
        const data = {
            email: String(email).trim().toLowerCase(),
            password,
        };

        if (!data.email) {
            setEmailError('Enter your e-mail');
            error = true;
        }
        if (!data.password) {
            setPasswordError('Enter your password');
            error = true;
        }

        if (!error) {
            setPostLoading(true);
            api.post('/shop/login/', data)
                .then(response => {
                    if (response.status === 200) {
                        clearFields();
                        const user = response.data.data.user;
                        const token = response.data.data.token;
                        handleAdmin(user, token);
                        handleStoreCmToken(user.id);
                        toast({ type: SUCCESS, message: response.data.message });
                    }
                })
                .catch(error => {
                    error.response
                        ? toast({ type: ERROR, message: error.response.data.message })
                        : toast({ type: ERROR, message: error.message });
                })
                .finally(() => {
                    setPostLoading(false);
                });
        }
    };

    const handlePasswordReset = () => {
        if (email === '') {
            setEmailError('Enter your email');
            return;
        }
        const expiryDate = new Date(Date.now() + 10 * 60 * 1000);
        const otpExpiryTime =
            expiryDate.getFullYear() + '-' +
            String(expiryDate.getMonth() + 1).padStart(2, '0') + '-' +
            String(expiryDate.getDate()).padStart(2, '0') + ' ' +
            String(expiryDate.getHours()).padStart(2, '0') + ':' +
            String(expiryDate.getMinutes()).padStart(2, '0') + ':' +
            String(expiryDate.getSeconds()).padStart(2, '0');
        const data = {
            email: email,
        };
        setPostLoading(true);
        api.post(`/shop/resetPassword/`, data)
            .then(response => {
                if (response.status === 200) {
                    setCurrentView('otp');
                    toast({ type: SUCCESS, message: 'OTP sent to your email successfully' });
                }
            })
            .catch(error => {
                toast({ type: ERROR, message: error.response?.data?.message || 'Unable to send mail. Please, check your email.' });
            })
            .finally(() => setPostLoading(false));
    };

    const submitOtp = () => {
        if (otpInput === '') {
            setOtpError('OTP is required');
            return;
        }
        setPostLoading(true);
        api.post(`/shop/?flag=verifyOtp`, { email, otp: Number(otpInput) })
            .then(response => {
                if (response.status === 200) {
                    toast({ type: SUCCESS, message: 'OTP verified successfully' });
                    setCurrentView('set-password');
                }
            })
            .catch(error => {
                toast({ type: ERROR, message: error.response?.data?.message || 'Invalid OTP' });
            })
            .finally(() => setPostLoading(false));
    };

    const submitNewPassword = () => {
        if (newPassword === '' || confirmPassword === '') {
            setNewPasswordError('Both fields are required');
            return;
        }
        if (newPassword !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match');
            return;
        }
        const payload = { email, otp: otpInput, password: newPassword };
        setPostLoading(true);
        api.put(`/shop/resetPassword/?flag=updatePassword`, payload)
            .then(response => {
                if (response.status === 200) {
                    toast({ type: SUCCESS, message: 'Password updated successfully' });
                    setCurrentView('login');
                }
            })
            .catch(error => {
                toast({ type: ERROR, message: error.response?.data?.message || 'Something went wrong' });
            })
            .finally(() => setPostLoading(false));
    };

    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

    return (
        <div className="vh-100 d-flex justify-content-center align-items-center bg-primary">
            <div className="card p-4 shadow" style={{ maxWidth: '400px', width: '100%' }}>
                <div className="container">
                    <h2 className="text-center">Admin Login</h2>
                    <p className="text-50 text-center">Sign in to continue</p>

                    {/* Login Form */}
                    {currentView === 'login' && (
                        <form className="mt-4" action="#">
                            <div className="form-group mb-4">
                                <label>Email Id</label>
                                <input
                                    type="email"
                                    className={`form-control ${emailError ? 'border-danger' : ''}`}
                                    placeholder="Enter Your Email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                                <div className="text-danger small">{emailError}</div>
                            </div>
                            <div className="form-group mb-4">
                                <div className="d-flex justify-content-between">
                                    <label>Password</label>
                                    <Link to="#" onClick={e => preventClick(e, () => setShowLoginPassword(v => !v))}>
                                        {showLoginPassword ? 'Hide' : 'Show'} Password
                                    </Link>
                                </div>
                                <input
                                    type={showLoginPassword ? "text" : "password"}
                                    className={`form-control ${passwordError ? 'border-danger' : ''}`}
                                    placeholder="Enter Password"
                                    value={password}
                                    onChange={e => handlePasswordChange(e.target.value)}
                                />
                                <div className="text-danger small">{passwordError}</div>
                                <div className="text-end mt-1">
                                    <Link to="#" onClick={() => setCurrentView('forgot')}>
                                        Forgot Password?
                                    </Link>
                                </div>
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={e => preventClick(e, handleSignIn)}
                                disabled={postLoading}
                            >
                                {postLoading ? 'Please wait...' : 'SIGN IN'}
                            </button>
                        </form>
                    )}

                    {/* Forgot Password */}
                    {currentView === 'forgot' && (
                        <div className="mt-4">
                            <h4>Reset Password</h4>
                            <div className="form-group mb-4">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    className={`form-control ${emailError ? 'border-danger' : ''}`}
                                    placeholder="Enter Email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                                <div className="text-danger small">{emailError}</div>
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={e => preventClick(e, handlePasswordReset)}
                                disabled={postLoading}
                            >
                                {postLoading ? 'Sending OTP...' : 'Send OTP'}
                            </button>
                            <button
                                className="btn btn-link w-100"
                                onClick={() => setCurrentView('login')}
                            >
                                Back to Login
                            </button>
                        </div>
                    )}

                    {/* OTP Input */}
                    {currentView === 'otp' && (
                        <div className="mt-4">
                            <h4>Enter OTP</h4>
                            <div className="form-group mb-4">
                                <label>OTP</label>
                                <input
                                    type="text"
                                    className={`form-control ${otpError ? 'border-danger' : ''}`}
                                    placeholder="Enter OTP"
                                    value={otpInput}
                                    onChange={e => setOtpInput(e.target.value)}
                                />
                                <div className="text-danger small">{otpError}</div>
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={e => preventClick(e, submitOtp)}
                                disabled={postLoading}
                            >
                                Submit
                            </button>
                        </div>
                    )}

                    {/* Set New Password */}
                    {currentView === 'set-password' && (
                        <div className="mt-4">
                            <h4>Set New Password</h4>
                            <div className="form-group mb-4">
                                <div className="d-flex justify-content-between">
                                    <label>New Password</label>
                                    <Link to="#" onClick={e => preventClick(e, () => setShowResetPassword(v => !v))}>
                                        {showResetPassword ? 'Hide' : 'Show'} Password
                                    </Link>
                                </div>
                                <input
                                    type={showResetPassword ? "text" : "password"}
                                    className={`form-control ${newPasswordError ? 'border-danger' : ''}`}
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                />
                                <div className="text-danger small">{newPasswordError}</div>
                            </div>
                            <div className="form-group mb-4">
                                <div className="d-flex justify-content-between">
                                    <label>Confirm Password</label>
                                    <Link to="#" onClick={e => preventClick(e, () => setShowResetConfirmPassword(v => !v))}>
                                        {showResetConfirmPassword ? 'Hide' : 'Show'} Password
                                    </Link>
                                </div>
                                <input
                                    type={showResetConfirmPassword ? "text" : "password"}
                                    className={`form-control ${confirmPasswordError ? 'border-danger' : ''}`}
                                    placeholder="Confirm password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                />
                                <div className="text-danger small">{confirmPasswordError}</div>
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={e => preventClick(e, submitNewPassword)}
                                disabled={postLoading}
                            >
                                Submit
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
